import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT, usePageTitle, Trans } from "@/i18n/LanguageContext";
import { api, resolveImg } from "@/lib/api";

const ATELIER =
  "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1600&q=80";
const MAKER =
  "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?auto=format&fit=crop&w=1600&q=80";

export default function About() {
  const t = useT();
  usePageTitle(t("meta.title.about"));
  // Images are curated from the admin console; fall back to the defaults when unset.
  const [intro, setIntro] = useState(ATELIER);
  const [visit, setVisit] = useState(MAKER);

  useEffect(() => {
    api.get("/homepage")
      .then((r) => {
        if (r.data.about_intro_image) setIntro(resolveImg(r.data.about_intro_image, 1200));
        if (r.data.about_visit_image) setVisit(resolveImg(r.data.about_visit_image, 1200));
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-20" data-testid="about-hero">
        <p className="overline reveal">{t("about.overline")}</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] mt-6 reveal reveal-delay-1 max-w-5xl">
          <Trans tKey="about.title" />
        </h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-12 gap-y-10 md:gap-10 pb-24" data-testid="about-intro">
        <div className="col-span-12 md:col-span-5">
          <img src={intro} alt="" className="w-full aspect-[3/4] object-cover" />
        </div>
        <div className="col-span-12 md:col-span-6 md:col-start-7">
          <p className="overline">{t("about.intro.overline")}</p>
          <h2 className="font-serif text-4xl lg:text-5xl mt-4 leading-[1]">
            <Trans tKey="about.intro.title" />
          </h2>
          <div className="text-melilux-cream/70 mt-8 leading-relaxed space-y-6 max-w-lg">
            <p>{t("about.intro.p1")}</p>
            <p>{t("about.intro.p2")}</p>
            <p>{t("about.intro.p3")}</p>
          </div>
        </div>
      </section>

      <section className="border-y border-white/10" data-testid="about-pillars">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
          {[
            { n: "01", title: t("about.p1.title"), body: t("about.p1.body") },
            { n: "02", title: t("about.p2.title"), body: t("about.p2.body") },
            { n: "03", title: t("about.p3.title"), body: t("about.p3.body") },
          ].map((p) => (
            <div key={p.n} className="reveal">
              <div className="font-serif text-5xl text-melilux-gold">{p.n}</div>
              <h3 className="font-serif text-3xl mt-6">{p.title}</h3>
              <p className="text-melilux-cream/65 mt-4 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-24 md:py-32 text-center" data-testid="about-quote">
        <p className="overline">{t("about.quote.overline")}</p>
        <blockquote className="font-serif text-3xl md:text-5xl leading-tight mt-8">
          <em className="serif-italic">"</em>
          <Trans tKey="about.quote.text" />
          <em className="serif-italic">"</em>
        </blockquote>
        <p className="overline mt-10">{t("about.quote.author")}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-12 gap-y-10 md:gap-10 pb-32" data-testid="about-cta">
        <div className="col-span-12 md:col-span-7 aspect-[5/4]">
          <img src={visit} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="col-span-12 md:col-span-5 md:self-end">
          <p className="overline">{t("about.visit.overline")}</p>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1]">
            <Trans tKey="about.visit.title" />
          </h2>
          <p className="text-melilux-cream/65 mt-6 leading-relaxed">{t("about.visit.lead")}</p>
          <Link
            to="/contact"
            data-testid="about-contact-btn"
            className="inline-block mt-8 border border-melilux-gold text-melilux-gold px-8 py-4 text-[0.72rem] uppercase tracking-[0.3em] hover:bg-melilux-gold hover:text-melilux-bg transition-colors"
          >
            {t("about.visit.btn")}
          </Link>
        </div>
      </section>
    </>
  );
}
